import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getPlaylists,
  createPlaylist,
  updatePlaylist,
  deletePlaylist,
  getTracks,
  createTrack,
  updateTrack,
  deleteTrack,
  getAdminUsers,
  updateAdminUser,
  getStoriesForAdmin,
  createStory,
  updateStory,
  deleteStory,
  getRadioConfig,
  saveRadioConfig,
  type PlaylistApi,
  type TrackApi,
  type UserApi,
  type StoryApi,
} from '../../api/client';
import { getMediaType } from '../../utils/media';
import {
  DEFAULT_RADIO_CONFIG,
  mergeRadioConfig,
  type RadioConfig,
} from '../../utils/radioConfig';
import AdminRadioPanel from './components/AdminRadioPanel';

type AdminTab = 'playlists' | 'radio' | 'tracks' | 'users' | 'stories';

export default function Admin() {
  const { isAdmin, username, userId, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminTab>('playlists');
  const [playlists, setPlaylists] = useState<PlaylistApi[]>([]);
  const [tracks, setTracks] = useState<TrackApi[]>([]);
  const [users, setUsers] = useState<UserApi[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formVisibility, setFormVisibility] = useState<'public' | 'followers' | 'private'>('public');
  const [formTrackIds, setFormTrackIds] = useState<number[]>([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newVisibility, setNewVisibility] = useState<'public' | 'followers' | 'private'>('public');
  const [stories, setStories] = useState<StoryApi[]>([]);
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [showNewStoryForm, setShowNewStoryForm] = useState(false);
  const [newStoryTitle, setNewStoryTitle] = useState('');
  const [newStoryExcerpt, setNewStoryExcerpt] = useState('');
  const [newStoryBody, setNewStoryBody] = useState('');
  const [newStoryCover, setNewStoryCover] = useState('');
  const [newStoryPublished, setNewStoryPublished] = useState(true);
  const [editingStoryId, setEditingStoryId] = useState<number | null>(null);
  const [editStoryTitle, setEditStoryTitle] = useState('');
  const [editStoryExcerpt, setEditStoryExcerpt] = useState('');
  const [editStoryBody, setEditStoryBody] = useState('');
  const [editStoryCover, setEditStoryCover] = useState('');
  const [editStoryPublished, setEditStoryPublished] = useState(true);

  // ── Tracks tab state ──
  const [showNewTrackForm, setShowNewTrackForm] = useState(false);
  const [newTrackTitle, setNewTrackTitle] = useState('');
  const [newTrackArtist, setNewTrackArtist] = useState('');
  const [newTrackAlbum, setNewTrackAlbum] = useState('');
  const [newTrackDuration, setNewTrackDuration] = useState('');
  const [newTrackImage, setNewTrackImage] = useState('');
  const [newTrackAudioUrl, setNewTrackAudioUrl] = useState('');
  const [tracksLoading, setTracksLoading] = useState(false);
  const [editingTrackId, setEditingTrackId] = useState<number | null>(null);
  const [editTrackTitle, setEditTrackTitle] = useState('');
  const [editTrackArtist, setEditTrackArtist] = useState('');
  const [editTrackAlbum, setEditTrackAlbum] = useState('');
  const [editTrackDuration, setEditTrackDuration] = useState('');
  const [editTrackImage, setEditTrackImage] = useState('');
  const [editTrackAudioUrl, setEditTrackAudioUrl] = useState('');

  const [radioConfig, setRadioConfig] = useState<RadioConfig>(DEFAULT_RADIO_CONFIG);
  const [radioLoading, setRadioLoading] = useState(false);
  const [radioSaving, setRadioSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login', { replace: true });
      return;
    }
    load();
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin && activeTab === 'users') loadUsers();
  }, [isAdmin, activeTab]);

  useEffect(() => {
    if (isAdmin && activeTab === 'stories') loadStories();
  }, [isAdmin, activeTab]);

  useEffect(() => {
    if (isAdmin && activeTab === 'tracks') loadTracksTab();
  }, [isAdmin, activeTab]);

  useEffect(() => {
    if (isAdmin && activeTab === 'radio') loadRadioTab();
  }, [isAdmin, activeTab]);

  async function loadRadioTab() {
    setRadioLoading(true);
    setError('');
    try {
      const { config } = await getRadioConfig();
      setRadioConfig(mergeRadioConfig(config));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load radio configuration');
      setRadioConfig(DEFAULT_RADIO_CONFIG);
    } finally {
      setRadioLoading(false);
    }
  }

  async function handleSaveRadio(): Promise<boolean> {
    setRadioSaving(true);
    setError('');
    try {
      const { config } = await saveRadioConfig(mergeRadioConfig(radioConfig));
      setRadioConfig(mergeRadioConfig(config));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save radio configuration');
      return false;
    } finally {
      setRadioSaving(false);
    }
  }

  async function loadStories() {
    setStoriesLoading(true);
    setError('');
    try {
      const list = await getStoriesForAdmin();
      setStories(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stories');
    } finally {
      setStoriesLoading(false);
    }
  }

  async function loadUsers() {
    setUsersLoading(true);
    setError('');
    try {
      const list = await getAdminUsers();
      setUsers(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }

  async function handleUserRoleChange(u: UserApi, newRole: string) {
    if (u.role === newRole) return;
    setError('');
    try {
      const updated = await updateAdminUser(u.id, { role: newRole });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    }
  }

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [p, t] = await Promise.all([getPlaylists(), getTracks()]);
      setPlaylists(p);
      setTracks(t);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  const handleEdit = (pl: PlaylistApi) => {
    setEditingId(pl.id);
    setFormName(pl.name);
    setFormDesc(pl.description || '');
    setFormVisibility(pl.visibility || 'public');
    setFormTrackIds(pl.tracks.map((t) => t.id));
  };

  const handleSaveEdit = async () => {
    if (editingId == null) return;
    setError('');
    try {
      const updated = await updatePlaylist(editingId, {
        name: formName,
        description: formDesc,
        visibility: formVisibility,
        trackIds: formTrackIds,
      });
      setPlaylists((prev) => prev.map((p) => (p.id === editingId ? updated : p)));
      setEditingId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this playlist?')) return;
    setError('');
    try {
      await deletePlaylist(id);
      setPlaylists((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setError('');
    try {
      const created = await createPlaylist({
        name: newName.trim(),
        description: newDesc.trim(),
        visibility: newVisibility,
      });
      setPlaylists((prev) => [...prev, created]);
      setShowNewForm(false);
      setNewName('');
      setNewDesc('');
      setNewVisibility('public');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    }
  };

  const toggleTrackInForm = (trackId: number) => {
    setFormTrackIds((prev) =>
      prev.includes(trackId) ? prev.filter((id) => id !== trackId) : [...prev, trackId]
    );
  };

  const moveTrack = (index: number, dir: 1 | -1) => {
    const next = [...formTrackIds];
    const ni = index + dir;
    if (ni < 0 || ni >= next.length) return;
    [next[index], next[ni]] = [next[ni], next[index]];
    setFormTrackIds(next);
  };

  const handleCreateStory = async () => {
    if (!newStoryTitle.trim()) return;
    setError('');
    try {
      const created = await createStory({
        title: newStoryTitle.trim(),
        excerpt: newStoryExcerpt.trim(),
        body: newStoryBody.trim(),
        cover_image: newStoryCover.trim() || undefined,
        is_published: newStoryPublished,
      });
      setStories((prev) => [created, ...prev]);
      setShowNewStoryForm(false);
      setNewStoryTitle('');
      setNewStoryExcerpt('');
      setNewStoryBody('');
      setNewStoryCover('');
      setNewStoryPublished(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create story');
    }
  };

  const handleEditStory = (s: StoryApi) => {
    setEditingStoryId(s.id);
    setEditStoryTitle(s.title);
    setEditStoryExcerpt(s.excerpt || '');
    setEditStoryBody(s.body || '');
    setEditStoryCover(s.cover_image || '');
    setEditStoryPublished(s.is_published);
  };

  const handleSaveStory = async () => {
    if (editingStoryId == null) return;
    setError('');
    try {
      const updated = await updateStory(editingStoryId, {
        title: editStoryTitle.trim(),
        excerpt: editStoryExcerpt.trim(),
        body: editStoryBody.trim(),
        cover_image: editStoryCover.trim() || undefined,
        is_published: editStoryPublished,
      });
      setStories((prev) => prev.map((x) => (x.id === editingStoryId ? updated : x)));
      setEditingStoryId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update story');
    }
  };

  const handleDeleteStory = async (id: number) => {
    if (!confirm('Delete this story? Comments will be removed too.')) return;
    setError('');
    try {
      await deleteStory(id);
      setStories((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete story');
    }
  };

  // ── Track handlers ──
  async function loadTracksTab() {
    setTracksLoading(true);
    setError('');
    try {
      const list = await getTracks();
      setTracks(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tracks');
    } finally {
      setTracksLoading(false);
    }
  }

  const handleCreateTrack = async () => {
    if (!newTrackTitle.trim() || !newTrackArtist.trim()) return;
    setError('');
    try {
      const created = await createTrack({
        title: newTrackTitle.trim(),
        artist: newTrackArtist.trim(),
        album: newTrackAlbum.trim() || newTrackArtist.trim(),
        duration: newTrackDuration.trim() || '0:00',
        image: newTrackImage.trim() || '/logo.png',
        audio_url: newTrackAudioUrl.trim() || undefined,
      });
      setTracks((prev) => [created, ...prev]);
      setShowNewTrackForm(false);
      setNewTrackTitle('');
      setNewTrackArtist('');
      setNewTrackAlbum('');
      setNewTrackDuration('');
      setNewTrackImage('');
      setNewTrackAudioUrl('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create track');
    }
  };

  const handleDeleteTrack = async (id: number) => {
    if (!confirm('Delete this track? It will be removed from all playlists.')) return;
    setError('');
    try {
      await deleteTrack(id);
      setTracks((prev) => prev.filter((t) => t.id !== id));
      if (editingTrackId === id) setEditingTrackId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete track');
    }
  };

  const handleEditTrack = (t: TrackApi) => {
    setEditingTrackId(t.id);
    setEditTrackTitle(t.title);
    setEditTrackArtist(t.artist);
    setEditTrackAlbum(t.album);
    setEditTrackDuration(t.duration);
    setEditTrackImage(t.image);
    setEditTrackAudioUrl(t.audio_url?.trim() || '');
    setShowNewTrackForm(false);
  };

  const handleSaveTrackEdit = async () => {
    if (editingTrackId == null) return;
    if (!editTrackTitle.trim() || !editTrackArtist.trim()) return;
    setError('');
    try {
      const updated = await updateTrack(editingTrackId, {
        title: editTrackTitle.trim(),
        artist: editTrackArtist.trim(),
        album: editTrackAlbum.trim() || editTrackArtist.trim(),
        duration: editTrackDuration.trim() || '0:00',
        image: editTrackImage.trim() || '/logo.png',
        audio_url: editTrackAudioUrl.trim() || null,
      });
      setTracks((prev) => prev.map((x) => (x.id === editingTrackId ? updated : x)));
      setEditingTrackId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update track');
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 pt-14">
      <header className="bg-[#6B3B18] text-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Frequency Locus" className="w-10 h-10 rounded-full object-contain bg-white/10 p-0.5" />
            <div>
            <h1 className="text-xl font-bold">Admin</h1>
            <p className="text-amber-100/90 text-sm">Logged in as {username}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="px-3 py-1.5 bg-white/20 rounded-lg text-sm hover:bg-white/30"
            >
              App
            </button>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="px-3 py-1.5 bg-white/20 rounded-lg text-sm hover:bg-white/30"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 bg-red-100 border border-red-300 text-red-800 rounded-lg px-4 py-2 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveTab('playlists')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === 'playlists' ? 'bg-[#6B3B18] text-white' : 'bg-amber-100 text-amber-900 hover:bg-amber-200'}`}
          >
            Playlists
          </button>
          <button
            onClick={() => setActiveTab('radio')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === 'radio' ? 'bg-[#6B3B18] text-white' : 'bg-amber-100 text-amber-900 hover:bg-amber-200'}`}
          >
            Radio
          </button>
          <button
            onClick={() => setActiveTab('tracks')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === 'tracks' ? 'bg-[#6B3B18] text-white' : 'bg-amber-100 text-amber-900 hover:bg-amber-200'}`}
          >
            Tracks
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === 'users' ? 'bg-[#6B3B18] text-white' : 'bg-amber-100 text-amber-900 hover:bg-amber-200'}`}
          >
            Registered users
          </button>
          <button
            onClick={() => setActiveTab('stories')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === 'stories' ? 'bg-[#6B3B18] text-white' : 'bg-amber-100 text-amber-900 hover:bg-amber-200'}`}
          >
            Stories
          </button>
        </div>

        {activeTab === 'radio' ? (
          <AdminRadioPanel
            config={radioConfig}
            setConfig={setRadioConfig}
            loading={radioLoading}
            saving={radioSaving}
            onSave={handleSaveRadio}
            onReload={loadRadioTab}
          />
        ) : activeTab === 'tracks' ? (
          <>
            <h2 className="text-lg font-semibold text-gray-800 mb-1">Track Catalog</h2>
            <p className="text-sm text-gray-500 mb-4">
              Add tracks with a YouTube or SoundCloud link as the audio source.
              Paste the full URL (e.g. <code className="bg-amber-50 px-1 rounded text-xs">https://www.youtube.com/watch?v=…</code>).
            </p>
            {tracksLoading ? (
              <p className="text-gray-600">Loading tracks…</p>
            ) : (
              <>
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => { setShowNewTrackForm(true); setEditingTrackId(null); }}
                    className="px-4 py-2 bg-[#6B3B18] text-white rounded-lg text-sm font-medium hover:opacity-90"
                  >
                    + Add track
                  </button>
                </div>

                {showNewTrackForm && (
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-amber-100 mb-4">
                    <h3 className="font-medium text-gray-800 mb-3">New track</h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Title *</label>
                        <input value={newTrackTitle} onChange={(e) => setNewTrackTitle(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" placeholder="Track title" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Artist *</label>
                        <input value={newTrackArtist} onChange={(e) => setNewTrackArtist(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" placeholder="Artist name" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Album / Collection</label>
                        <input value={newTrackAlbum} onChange={(e) => setNewTrackAlbum(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" placeholder="Leave blank to use artist name" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Duration (e.g. 3:45)</label>
                        <input value={newTrackDuration} onChange={(e) => setNewTrackDuration(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" placeholder="3:45" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs text-gray-500 mb-1">Cover image URL</label>
                        <input value={newTrackImage} onChange={(e) => setNewTrackImage(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" placeholder="https://… (leave blank for app logo)" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs text-gray-500 mb-1">YouTube or SoundCloud URL *</label>
                        <input
                          value={newTrackAudioUrl}
                          onChange={(e) => setNewTrackAudioUrl(e.target.value)}
                          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full font-mono"
                          placeholder="https://www.youtube.com/watch?v=… or https://soundcloud.com/…"
                        />
                        {newTrackAudioUrl.trim() && (
                          <p className={`mt-1 text-xs ${
                            getMediaType(newTrackAudioUrl) === 'none' || getMediaType(newTrackAudioUrl) === 'audio'
                              ? 'text-amber-600'
                              : 'text-green-700'
                          }`}>
                            {getMediaType(newTrackAudioUrl) === 'youtube' && '✓ YouTube link detected'}
                            {getMediaType(newTrackAudioUrl) === 'soundcloud' && '✓ SoundCloud link detected'}
                            {(getMediaType(newTrackAudioUrl) === 'none' || getMediaType(newTrackAudioUrl) === 'audio') && '⚠ Not recognised as YouTube or SoundCloud'}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={handleCreateTrack}
                        disabled={!newTrackTitle.trim() || !newTrackArtist.trim()}
                        className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm disabled:opacity-50"
                      >
                        Add track
                      </button>
                      <button
                        onClick={() => { setShowNewTrackForm(false); setNewTrackTitle(''); setNewTrackArtist(''); setNewTrackAlbum(''); setNewTrackDuration(''); setNewTrackImage(''); setNewTrackAudioUrl(''); }}
                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {tracks.map((t) => {
                    const mType = getMediaType(t.audio_url);
                    if (editingTrackId === t.id) {
                      return (
                        <div key={t.id} className="bg-white rounded-xl p-4 shadow-sm border border-amber-200">
                          <h3 className="font-medium text-gray-800 mb-3">Edit track</h3>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Title *</label>
                              <input value={editTrackTitle} onChange={(e) => setEditTrackTitle(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Artist *</label>
                              <input value={editTrackArtist} onChange={(e) => setEditTrackArtist(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Album / Collection</label>
                              <input value={editTrackAlbum} onChange={(e) => setEditTrackAlbum(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Duration</label>
                              <input value={editTrackDuration} onChange={(e) => setEditTrackDuration(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" placeholder="3:45" />
                            </div>
                            <div className="sm:col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">Cover image URL</label>
                              <input value={editTrackImage} onChange={(e) => setEditTrackImage(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                            </div>
                            <div className="sm:col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">YouTube, SoundCloud, or direct audio URL</label>
                              <input
                                value={editTrackAudioUrl}
                                onChange={(e) => setEditTrackAudioUrl(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full font-mono"
                                placeholder="https://…"
                              />
                              {editTrackAudioUrl.trim() && (
                                <p className={`mt-1 text-xs ${
                                  getMediaType(editTrackAudioUrl) === 'none' || getMediaType(editTrackAudioUrl) === 'audio'
                                    ? 'text-amber-600'
                                    : 'text-green-700'
                                }`}>
                                  {getMediaType(editTrackAudioUrl) === 'youtube' && '✓ YouTube link detected'}
                                  {getMediaType(editTrackAudioUrl) === 'soundcloud' && '✓ SoundCloud link detected'}
                                  {getMediaType(editTrackAudioUrl) === 'audio' && '✓ Direct audio URL'}
                                  {(getMediaType(editTrackAudioUrl) === 'none') && '⚠ Empty or invalid URL'}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={handleSaveTrackEdit}
                              disabled={!editTrackTitle.trim() || !editTrackArtist.trim()}
                              className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingTrackId(null)}
                              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={t.id} className="bg-white rounded-xl border border-amber-100 shadow-sm p-3 flex items-center gap-3">
                        <img
                          src={t.image || '/logo.png'}
                          alt=""
                          className="h-10 w-10 rounded-lg object-cover shrink-0 bg-stone-100"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/logo.png'; }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-800 truncate">{t.title}</p>
                          <p className="text-xs text-gray-500 truncate">{t.artist} · {t.album}</p>
                          <p className={`text-xs mt-0.5 ${
                            mType === 'youtube' ? 'text-red-600' :
                            mType === 'soundcloud' ? 'text-orange-500' :
                            mType === 'audio' ? 'text-green-600' :
                            'text-gray-400'
                          }`}>
                            {mType === 'youtube' && '▶ YouTube'}
                            {mType === 'soundcloud' && '◈ SoundCloud'}
                            {mType === 'audio' && '♪ Audio file'}
                            {mType === 'none' && 'No audio linked'}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400 shrink-0">{t.duration}</span>
                        <div className="flex gap-1.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleEditTrack(t)}
                            className="px-2.5 py-1.5 bg-amber-100 text-amber-800 rounded-lg text-xs"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteTrack(t.id)}
                            className="px-2.5 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {tracks.length === 0 && !showNewTrackForm && !tracksLoading && (
                  <p className="text-gray-500 text-sm">No tracks yet. Add one above.</p>
                )}
              </>
            )}
          </>
        ) : activeTab === 'stories' ? (
          <>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">African Storytelling</h2>
            {storiesLoading ? (
              <p className="text-gray-600">Loading stories…</p>
            ) : (
              <>
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => setShowNewStoryForm(true)}
                    className="px-4 py-2 bg-[#6B3B18] text-white rounded-lg text-sm font-medium hover:opacity-90"
                  >
                    + New story
                  </button>
                </div>
                {showNewStoryForm && (
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-amber-100 mb-4">
                    <h3 className="font-medium text-gray-800 mb-3">New story</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Title *</label>
                        <input value={newStoryTitle} onChange={(e) => setNewStoryTitle(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" placeholder="Story title" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Excerpt (short summary)</label>
                        <input value={newStoryExcerpt} onChange={(e) => setNewStoryExcerpt(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" placeholder="Brief excerpt" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Body (full text)</label>
                        <textarea value={newStoryBody} onChange={(e) => setNewStoryBody(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full min-h-[120px]" placeholder="Story content" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Cover image URL</label>
                        <input value={newStoryCover} onChange={(e) => setNewStoryCover(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" placeholder="https://..." />
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={newStoryPublished} onChange={(e) => setNewStoryPublished(e.target.checked)} />
                        Published (visible on site)
                      </label>
                      <div className="flex gap-2">
                        <button onClick={handleCreateStory} className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm">Create</button>
                        <button onClick={() => { setShowNewStoryForm(false); setNewStoryTitle(''); setNewStoryExcerpt(''); setNewStoryBody(''); setNewStoryCover(''); }} className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm">Cancel</button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  {stories.map((s) => (
                    <div key={s.id} className="bg-white rounded-xl border border-amber-100 shadow-sm overflow-hidden">
                      {editingStoryId === s.id ? (
                        <div className="p-4 space-y-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Title</label>
                            <input value={editStoryTitle} onChange={(e) => setEditStoryTitle(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Excerpt</label>
                            <input value={editStoryExcerpt} onChange={(e) => setEditStoryExcerpt(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Body</label>
                            <textarea value={editStoryBody} onChange={(e) => setEditStoryBody(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full min-h-[100px]" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Cover image URL</label>
                            <input value={editStoryCover} onChange={(e) => setEditStoryCover(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                          </div>
                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={editStoryPublished} onChange={(e) => setEditStoryPublished(e.target.checked)} />
                            Published
                          </label>
                          <div className="flex gap-2">
                            <button onClick={handleSaveStory} className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm">Save</button>
                            <button onClick={() => setEditingStoryId(null)} className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-800">{s.title}</h3>
                            {s.excerpt && <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">{s.excerpt}</p>}
                            <p className="text-xs text-gray-500 mt-1">
                              {s.is_published ? 'Published' : 'Draft'} · {s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}
                            </p>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <a href={`/stories/${s.id}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm">View</a>
                            <button onClick={() => handleEditStory(s)} className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg text-sm">Edit</button>
                            <button onClick={() => handleDeleteStory(s.id)} className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm">Delete</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {stories.length === 0 && !showNewStoryForm && !storiesLoading && (
                  <p className="text-gray-500 text-sm">No stories yet. Create one to show in the African Storytelling section.</p>
                )}
              </>
            )}
          </>
        ) : activeTab === 'users' ? (
          <>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Registered users</h2>
            {usersLoading ? (
              <p className="text-gray-600">Loading users…</p>
            ) : (
              <div className="bg-white rounded-xl border border-amber-100 shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-amber-50/80 border-b border-amber-100">
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Username</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Role</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Joined</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => {
                      const isSelf = userId !== null && u.id === userId;
                      return (
                        <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                          <td className="px-4 py-3 text-gray-800">
                            {u.username}
                            {isSelf && <span className="ml-1.5 text-xs text-stone-400">(you)</span>}
                          </td>
                          <td className="px-4 py-3">
                            {isSelf ? (
                              <span className="inline-flex items-center gap-1.5 text-sm text-stone-500" title="Cannot change your own role">
                                {u.role ?? 'user'}
                              </span>
                            ) : (
                              <select
                                value={u.role ?? 'user'}
                                onChange={(e) => handleUserRoleChange(u, e.target.value)}
                                className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white"
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="superuser">Superuser</option>
                              </select>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-500">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-500">—</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {users.length === 0 && !usersLoading && (
                  <p className="px-4 py-6 text-gray-500 text-center text-sm">No registered users yet.</p>
                )}
              </div>
            )}
          </>
        ) : loading ? (
          <p className="text-gray-600">Loading…</p>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Playlists</h2>
              <button
                onClick={() => setShowNewForm(true)}
                className="px-4 py-2 bg-[#6B3B18] text-white rounded-lg text-sm font-medium hover:opacity-90"
              >
                + New playlist
              </button>
            </div>

            {showNewForm && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-amber-100 mb-4">
                <h3 className="font-medium text-gray-800 mb-3">New playlist</h3>
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Name</label>
                    <input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-48"
                      placeholder="Playlist name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Description</label>
                    <input
                      value={newDesc}
                      onChange={(e) => setNewDesc(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
                      placeholder="Optional description"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Visibility</label>
                    <select
                      value={newVisibility}
                      onChange={(e) => setNewVisibility(e.target.value as 'public' | 'followers' | 'private')}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-44 bg-white"
                    >
                      <option value="public">Public</option>
                      <option value="followers">Followers only</option>
                      <option value="private">Private</option>
                    </select>
                  </div>
                  <button
                    onClick={handleCreate}
                    className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => { setShowNewForm(false); setNewName(''); setNewDesc(''); setNewVisibility('public'); }}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {playlists.map((pl) => (
                <div
                  key={pl.id}
                  className="bg-white rounded-xl border border-amber-100 shadow-sm overflow-hidden"
                >
                  {editingId === pl.id ? (
                    <div className="p-4">
                      <div className="grid gap-3 mb-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Name</label>
                          <input
                            value={formName}
                            onChange={(e) => setFormName(e.target.value)}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Description</label>
                          <input
                            value={formDesc}
                            onChange={(e) => setFormDesc(e.target.value)}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Visibility</label>
                          <select
                            value={formVisibility}
                            onChange={(e) => setFormVisibility(e.target.value as 'public' | 'followers' | 'private')}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full bg-white"
                          >
                            <option value="public">Public</option>
                            <option value="followers">Followers only</option>
                            <option value="private">Private</option>
                          </select>
                        </div>
                      </div>
                      <div className="mb-3">
                        <span className="text-xs text-gray-500">Tracks (check to include, order below)</span>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {tracks.map((t) => (
                            <label
                              key={t.id}
                              className="inline-flex items-center gap-2 px-2 py-1 bg-gray-50 rounded border text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={formTrackIds.includes(t.id)}
                                onChange={() => toggleTrackInForm(t.id)}
                              />
                              {t.title} – {t.artist}
                            </label>
                          ))}
                          {tracks.length === 0 && (
                            <p className="text-sm text-gray-500">No tracks in catalog. Add tracks via API or seed DB.</p>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mb-1">Order (use arrows)</div>
                      <ul className="space-y-1 mb-4">
                        {formTrackIds.map((tid, i) => {
                          const t = tracks.find((x) => x.id === tid) || playlists.flatMap((p) => p.tracks).find((x) => x.id === tid);
                          return (
                            <li key={tid} className="flex items-center gap-2 text-sm">
                              <button type="button" onClick={() => moveTrack(i, -1)} className="text-gray-400 hover:text-gray-600">↑</button>
                              <button type="button" onClick={() => moveTrack(i, 1)} className="text-gray-400 hover:text-gray-600">↓</button>
                              <span>{t ? `${t.title} – ${t.artist}` : `Track #${tid}`}</span>
                            </li>
                          );
                        })}
                      </ul>
                      <div className="flex gap-2">
                        <button onClick={handleSaveEdit} className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm">Save</button>
                        <button onClick={() => setEditingId(null)} className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="p-4 flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-800">{pl.name}</h3>
                          {pl.description && <p className="text-sm text-gray-600">{pl.description}</p>}
                          <p className="text-xs text-gray-500 mt-1">
                            {pl.tracks.length} tracks · Visibility: {pl.visibility || 'public'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(pl)}
                            className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(pl.id)}
                            className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      {pl.tracks.length > 0 && (
                        <ul className="border-t border-gray-100 px-4 py-2 bg-gray-50/50 text-sm text-gray-600">
                          {pl.tracks.slice(0, 5).map((t) => (
                            <li key={t.id}>{t.title} – {t.artist}</li>
                          ))}
                          {pl.tracks.length > 5 && <li>+{pl.tracks.length - 5} more</li>}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            {playlists.length === 0 && !showNewForm && (
              <p className="text-gray-500 text-sm">No playlists yet. Create one or seed the database.</p>
            )}
          </>
        )}
      </main>
    </div>
  );
}
